import { useState, useEffect } from 'react';
import { Trophy, Music, Zap, Gift, Megaphone, Clock, RotateCcw, XCircle, Loader2 } from 'lucide-react';
import { Prize, subscribeToDJPrizes, revokePrize, reassignPrize, checkExpiredPrizes } from '../../services/prizes';
import toast from 'react-hot-toast';

interface PrizeManagePanelProps {
  eventId: string;
}

const TYPE_ICONS: Record<string, typeof Music> = {
  pick_next_song: Music,
  auto_queue_bypass: Zap,
  custom: Gift,
  shoutout: Megaphone,
};

const STATUS_COLORS: Record<string, string> = {
  configured: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  awarded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  claimed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  revoked: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
};

function TimeRemaining({ expiresAt }: { expiresAt?: string }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('Expired');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!remaining) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <Clock className="w-3 h-3" />
      {remaining}
    </span>
  );
}

function PrizeCard({ prize }: { prize: Prize }) {
  const [reassigning, setReassigning] = useState(false);
  const [newWinnerId, setNewWinnerId] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const Icon = TYPE_ICONS[prize.type] || Gift;

  const handleRevoke = async () => {
    try {
      await revokePrize(prize.id);
      toast.success('Prize revoked');
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const handleReassign = async () => {
    if (!newWinnerId.trim()) {
      toast.error('Enter an attendee ID');
      return;
    }
    try {
      await reassignPrize(prize.id, newWinnerId.trim(), newNickname.trim() || undefined);
      toast.success('Prize reassigned');
      setReassigning(false);
      setNewWinnerId('');
      setNewNickname('');
    } catch {
      toast.error('Failed to reassign');
    }
  };

  return (
    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
          {prize.type === 'custom' ? (prize.customText || 'Custom Prize') : prize.type.replace(/_/g, ' ')}
        </span>
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[prize.status]}`}>
          {prize.status}
        </span>
      </div>

      {prize.winnerNickname && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
          Winner: <span className="font-medium">{prize.winnerNickname}</span>
        </p>
      )}

      {prize.status === 'awarded' && (
        <TimeRemaining expiresAt={prize.expiresAt} />
      )}

      {prize.status === 'claimed' && prize.claimData?.songTitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Picked: {prize.claimData.songTitle} — {prize.claimData.songArtist}
        </p>
      )}

      <div className="flex gap-2 mt-2">
        {prize.status === 'awarded' && (
          <button
            onClick={handleRevoke}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
          >
            <XCircle className="w-3 h-3" />
            Revoke
          </button>
        )}
        {prize.status === 'expired' && !reassigning && (
          <button
            onClick={() => setReassigning(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Reassign
          </button>
        )}
      </div>

      {reassigning && (
        <div className="mt-2 space-y-2">
          <input
            type="text"
            value={newWinnerId}
            onChange={(e) => setNewWinnerId(e.target.value)}
            placeholder="Attendee ID"
            className="input w-full text-xs"
          />
          <input
            type="text"
            value={newNickname}
            onChange={(e) => setNewNickname(e.target.value)}
            placeholder="Nickname (optional)"
            className="input w-full text-xs"
          />
          <div className="flex gap-2">
            <button onClick={() => setReassigning(false)} className="btn btn-secondary text-xs py-1 flex-1">
              Cancel
            </button>
            <button onClick={handleReassign} className="btn btn-primary text-xs py-1 flex-1">
              Assign
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PrizeManagePanel({ eventId }: PrizeManagePanelProps) {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToDJPrizes(eventId, (allPrizes) => {
      setPrizes(allPrizes);
      setLoading(false);
    });

    checkExpiredPrizes(eventId).catch(() => {});

    return () => unsubscribe();
  }, [eventId]);

  const active = prizes.filter(p => p.status === 'awarded' || p.status === 'configured');
  const claimed = prizes.filter(p => p.status === 'claimed');
  const expired = prizes.filter(p => p.status === 'expired');
  const revoked = prizes.filter(p => p.status === 'revoked');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (prizes.length === 0) {
    return (
      <div className="text-center py-8">
        <Trophy className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No prizes yet</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Prizes are created through engagement games</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" />
        <h3 className="font-semibold text-gray-900 dark:text-white">Prizes</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">({prizes.length})</span>
      </div>

      {active.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Active</h4>
          <div className="space-y-2">
            {active.map(p => <PrizeCard key={p.id} prize={p} />)}
          </div>
        </div>
      )}

      {claimed.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Claimed</h4>
          <div className="space-y-2">
            {claimed.map(p => <PrizeCard key={p.id} prize={p} />)}
          </div>
        </div>
      )}

      {expired.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Expired</h4>
          <div className="space-y-2">
            {expired.map(p => <PrizeCard key={p.id} prize={p} />)}
          </div>
        </div>
      )}

      {revoked.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Revoked</h4>
          <div className="space-y-2">
            {revoked.map(p => <PrizeCard key={p.id} prize={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
